import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { resolveLocationForHostname, DEFAULT_FALLBACK_LOCATION } from '../config/domain-location.config';

/**
 * Resolves the current site location from the browser hostname.
 * Used for multi-domain sites (e.g. aruba-adventures.com vs bonaire-excursions.com).
 */
@Injectable({ providedIn: 'root' })
export class LocationContextService {
  private readonly _location$ = new BehaviorSubject<string>(DEFAULT_FALLBACK_LOCATION);
  private _resolved = false;

  readonly location$: Observable<string> = this._location$;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    this.resolveLocation();
  }

  /** Current location identifier (e.g. "aruba", "bonaire", "default") */
  get location(): string {
    return this._location$.value;
  }

  /** Whether the location has been resolved from the current environment */
  get isResolved(): boolean {
    return this._resolved;
  }

  /** Resolve location from hostname. Safe to call on server (no-op, uses fallback). */
  private resolveLocation(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this._location$.next(DEFAULT_FALLBACK_LOCATION);
      this._resolved = true;
      return;
    }

    try {
      const hostname = window.location.hostname;
      const resolved = resolveLocationForHostname(hostname);
      this._location$.next(resolved);
      this._resolved = true;
    } catch {
      this._location$.next(DEFAULT_FALLBACK_LOCATION);
      this._resolved = true;
    }
  }
}
